import { Checkbox, FormControlLabel, Modal } from "@mui/material";
import { Box } from "@mui/system";
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import CustomButton from "../Buttons/Button";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import SelectField from "../inputs/SelectField";
import CustomInput from "../inputs/CustomInput";
import HalDatePicker from "../inputs/HalDatePicker";
import SelectMember from "./SelectMember";
import { payoutOption } from "../../config/data";
import { mudarabahPayoutSchema } from "../../Helper/validationSchemas";
import { buildMudarabahPayload } from "../../Helper/mudarabahPayout";
import { submitMudarabahPayout } from "../../store/actions/disbursement/submitMudarabahPayout";

const selectSx = {
  width: "100%",
  height: "48px",
  borderRadius: "7px",
  borderColor: "#D0D5DD",
  color: "#101828",
};

const repaymentOptions = [
  // { label: "Profit + Capital", value: "profit_capital" },
  { label: "Profit Only", value: "profit_only" },
  { label: "Capital Only", value: "capital_only" },
];

const payoutTypeOptions = [
  { label: "Full", value: "full" },
  { label: "Partial", value: "partial" },
];

function MudarabahPayoutModal({
  open,
  onClose,
  cycle,
  invID,
  currency,
  refresh,
  setOpenModal,
}) {
  const { id: investmentId } = useParams();
  const [showMembers, setShowMembers] = useState(false);
  const loading = useSelector((state) => state?.disbursement?.pending);
  const investment = useSelector((state) => state?.investment?.investment);

  const dispatch = useDispatch();

  const handleSubmit = (values) => {
    // "Selected Users" hands over to SelectMember, which submits with the
    // ids the admin ticks.
    if (values?.beneficiary?.value !== "all") {
      form.setSubmitting(false);
      setShowMembers(true);
      return;
    }

    const beneficiaries = (
      cycle?.investors ||
      investment?.investments ||
      investment?.investors?.map((investor) => ({
        ...investor,
        user_id: investor?.id,
      }))
    )?.map((item) => item?.user_id);

    dispatch(
      submitMudarabahPayout({
        data: buildMudarabahPayload({
          values,
          cycle,
          invID: invID || investmentId,
          beneficiaries,
        }),
        callback: () => {
          form.setSubmitting(false);
          refresh && refresh();
          setOpenModal(false);
        },
        fail: () => {
          form.setSubmitting(false);
        },
      }),
    );
  };

  const form = useFormik({
    initialValues: {
      beneficiary: {
        value: "selected_users",
        label: "Selected Users",
      },
      roi_percentage: "",
      payout_date: dayjs(new Date()),
      payout_type: payoutTypeOptions[0],
      repayment_type: repaymentOptions[0],
      amount_to_disburse: 0,
      pay_everyone_partially: false,
      capital_amount: 0,
      profit_amount: 0,
      installment_type: "",
      narration: "",
      currency: { value: currency, label: currency },
      investment_product_id: invID || investmentId,
    },
    onSubmit: handleSubmit,
    validationSchema: mudarabahPayoutSchema,
  });

  useEffect(() => {
    form.setFieldValue("currency", { value: currency, label: currency });
  }, [currency]);

  const isPartialProfitCapital =
    form?.values?.payout_type?.value === "partial" &&
    form?.values?.repayment_type?.value === "profit_capital";

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="custom-modal-title"
        aria-describedby="custom-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxHeight: "90vh",
            overflowY: "auto",
            bgcolor: "background.paper",
            borderRadius: "10px",
            boxShadow: 24,
            p: 2,
          }}
        >
          <IconButton
            aria-label="close"
            sx={{
              position: "absolute",
              top: 19,
              right: 10,
            }}
            onClick={onClose}
          >
            <ClearIcon />
          </IconButton>

          <div className="w-full p-5">
            <h1 className="font-[500] text-2xl mb-4">Mudarabah Payout</h1>

            <div className="w-full items-center mr-4">
              <div className="mb-4">
                <label>ROI Percentage</label>
                <CustomInput
                  id="roi_percentage"
                  width="100%"
                  form={form}
                  type="number"
                  placeholder={"Enter ROI Percentage"}
                  height="12px"
                  mt="10"
                  mb="2px"
                  borderRadius={"4px"}
                  className="mobile-input-width"
                />
              </div>

              <div className="mb-4">
                <label>Payout date</label>
                <HalDatePicker
                  type={2}
                  label="Payout Date"
                  id={"payout_date"}
                  form={form}
                  small
                  rightImage
                  className="-mr-5"
                />
              </div>

              <label>Payout Type</label>
              <div className="mb-4">
                <SelectField
                  id="payout_type"
                  options={payoutTypeOptions}
                  form={form}
                  sx={selectSx}
                  fw={400}
                  fz={"16px"}
                />
              </div>

              <label>Repayment Type</label>
              <div className="mb-4">
                <SelectField
                  id="repayment_type"
                  options={repaymentOptions}
                  form={form}
                  sx={selectSx}
                  fw={400}
                  fz={"16px"}
                />
              </div>

              <div className="mb-4">
                <label>Amount to Disburse</label>
                <CustomInput
                  fullWidth
                  height="18px"
                  id="amount_to_disburse"
                  type={"currency"}
                  form={form}
                  placeholder={"Enter Amount"}
                  borderColor="#D0D5DD"
                  mt="10px"
                />
              </div>

              {isPartialProfitCapital ? (
                <>
                  <FormControlLabel
                    sx={{ mb: 1 }}
                    control={
                      <Checkbox
                        checked={!!form?.values?.pay_everyone_partially}
                        onChange={(event) =>
                          form.setFieldValue(
                            "pay_everyone_partially",
                            event.target.checked,
                          )
                        }
                        sx={{
                          color: "#159AA8",
                          "&.Mui-checked": { color: "#159AA8" },
                        }}
                      />
                    }
                    label={
                      <span className="text-sm text-[#344054]">
                        Make partial payment to everyone
                      </span>
                    }
                  />

                  {form?.values?.pay_everyone_partially ? (
                    <></>
                  ) : (
                    <>
                      <div className="mb-4">
                        <label>Capital amount</label>
                        <CustomInput
                          id="capital_amount"
                          width="100%"
                          form={form}
                          type="currency"
                          placeholder={"Enter Amount"}
                          height="12px"
                          mt="10"
                          mb="2px"
                          borderRadius={"4px"}
                          className="mobile-input-width"
                        />
                      </div>

                      <div className="mb-4">
                        <label>Profit amount</label>
                        <CustomInput
                          id="profit_amount"
                          width="100%"
                          form={form}
                          type="currency"
                          placeholder={"Enter Amount"}
                          height="12px"
                          mt="10"
                          mb="2px"
                          borderRadius={"4px"}
                          className="mobile-input-width"
                        />
                      </div>
                    </>
                  )}
                </>
              ) : (
                <></>
              )}

              <div className="mb-4">
                <label>Installment Type</label>
                <CustomInput
                  id="installment_type"
                  width="100%"
                  form={form}
                  placeholder={"e.g. Payout 1, Capital Return"}
                  height="12px"
                  mt="10"
                  mb="2px"
                  borderRadius={"4px"}
                  className="mobile-input-width"
                />
              </div>

              <div className="mb-4">
                <label>Narration</label>
                <CustomInput
                  id="narration"
                  width="100%"
                  form={form}
                  placeholder={"Enter Narration"}
                  height="12px"
                  mt="10"
                  mb="2px"
                  borderRadius={"4px"}
                  className="mobile-input-width"
                />
              </div>

              <div className="mt-6">
                <label>Payout To</label>
                <div className="mb-4">
                  <SelectField
                    id="beneficiary"
                    options={payoutOption}
                    form={form}
                    sx={selectSx}
                    fw={400}
                    fz={"16px"}
                  />
                </div>
              </div>

              <div className="flex mt-10 w-full items-center">
                <CustomButton
                  variant="outlined"
                  customColor="#fff"
                  children="Cancel"
                  width="100%"
                  onClick={onClose}
                  height="50px"
                  borderColor="#D0D5DD"
                  margin="0px 25px 0px 0px"
                  color="#159AA8"
                  iconWidth="22px"
                />

                <CustomButton
                  variant="contained"
                  customColor="#159AA8"
                  loading={loading || form.isSubmitting}
                  children={
                    form?.values?.beneficiary?.value === "selected_users"
                      ? "Continue"
                      : "Submit"
                  }
                  width="100%"
                  height="50px"
                  padding="10px"
                  disabled={!form?.isValid || loading}
                  onClick={form.handleSubmit}
                />
              </div>
            </div>
          </div>
        </Box>
      </Modal>

      <SelectMember
        open={showMembers}
        type={"Mudarabah"}
        invID={invID || investmentId}
        form={form}
        refresh={refresh}
        cycle={cycle}
        onCloseParent={onClose}
        onClose={() => setShowMembers(false)}
      />
    </>
  );
}

export default MudarabahPayoutModal;
